import {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import {Region} from 'react-native-maps';
import {Event} from '~types/api/event';
import {TABS, ORDER_BY, ORDER_DIR, EVENT_AGE_GROUP} from '~types/events';
import {useAllEvents} from './useAllEvents';
import {useTranslation} from 'react-i18next';
const haversine = require('haversine');

interface UseSimplifiedMapEventsProps {
  radius: number;
  isForKids?: boolean;
  selectedTimeframe?: any;
  globalInputValue?: string;
  isNeighbourhood?: boolean;
  isPyxiSelect?: boolean;
  isPyxiCore?: boolean;
  userLocation?: {latitude: number; longitude: number} | null;
  enabled?: boolean;
  eventServiceType?: 'THE_REST_TABLE' | 'CULTURAL_EDIT' | 'GENERAL' | null;
}

interface MapViewportState {
  center: {latitude: number; longitude: number};
  radius: number;
  lastFetchTime: number;
  zoomLevel?: number;
}

/**
 * Simplified map events hook that fetches events directly from API
 */
export const useSimplifiedMapEvents = ({
  radius,
  isForKids,
  selectedTimeframe,
  globalInputValue,
  isNeighbourhood,
  isPyxiSelect,
  isPyxiCore,
  userLocation,
  enabled = true,
  eventServiceType,
}: UseSimplifiedMapEventsProps) => {
  const [mapViewport, setMapViewport] = useState<MapViewportState | null>(null);
  const [currentRadius, setCurrentRadius] = useState(radius);
  const lastFetchRef = useRef<MapViewportState | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxRadius = useRef(1500); // Maximum radius of 1500km
  const {t} = useTranslation(undefined, { lng: 'en' });

  /**
   * Calculate radius based on zoom level
   * Lower zoom (zoomed out) = larger radius
   * Higher zoom (zoomed in) = smaller radius
   */
  const calculateRadiusFromZoom = useCallback((zoomLevel: number): number => {
    // Zoom levels typically range from 1-20
    // Zoom 1 = very zoomed out (world view)
    // Zoom 20 = very zoomed in (building level)

    // Base radius calculation: higher zoom = smaller radius
    let calculatedRadius;

    if (zoomLevel <= 8) {
      // Very zoomed out - use large radius (200-500km)
      calculatedRadius = Math.max(200, 500 - (zoomLevel - 1) * 50);
    } else if (zoomLevel <= 12) {
      // Medium zoom - moderate radius (50-200km)
      calculatedRadius = Math.max(50, 200 - (zoomLevel - 8) * 37.5);
    } else if (zoomLevel <= 16) {
      // Zoomed in - smaller radius (10-50km)
      calculatedRadius = Math.max(10, 50 - (zoomLevel - 12) * 10);
    } else {
      // Very zoomed in - minimum radius (5-10km)
      calculatedRadius = Math.max(5, 10 - (zoomLevel - 16) * 1.25);
    }

    // Ensure we don't exceed maximum radius and round to integer for API compatibility
    const finalRadius = Math.round(Math.min(calculatedRadius, maxRadius.current));

    console.log(
      `🔍 [MAP] Calculated radius from zoom: zoom=${zoomLevel}, radius=${finalRadius}km (rounded to integer)`,
    );
    return finalRadius;
  }, []);

  /**
   * Calculate zoom level from region
   */
  const calculateZoomLevel = useCallback((region: Region): number => {
    // Simple zoom calculation based on latitude delta
    // This is an approximation - more accurate would use map dimensions
    const zoom = Math.round(Math.log(360 / region.latitudeDelta) / Math.LN2);
    return Math.max(1, Math.min(20, zoom)); // Clamp between 1-20
  }, []);

  /**
   * Check if we need to fetch new events based on viewport change
   */
  const shouldFetchNewEvents = useCallback(
    (newViewport: MapViewportState): boolean => {
      if (!lastFetchRef.current) {
        return true;
      }

      const lastFetch = lastFetchRef.current;

      // Calculate distance moved
      const distance = haversine(
        {latitude: lastFetch.center.latitude, longitude: lastFetch.center.longitude},
        {latitude: newViewport.center.latitude, longitude: newViewport.center.longitude},
        {unit: 'km'},
      );

      // Thresholds for fetching new events
      const distanceThreshold = Math.max(radius * 0.3, 2); // 30% of radius or 2km minimum
      const radiusThreshold = 100; // 100km radius change threshold
      const timeThreshold = 5 * 60 * 1000; // 5 minutes

      const shouldFetch =
        distance > distanceThreshold ||
        Math.abs(newViewport.radius - lastFetch.radius) > radiusThreshold ||
        Date.now() - lastFetch.lastFetchTime > timeThreshold;

      console.log(`🗺️ [SIMPLIFIED] Should fetch new events:`, {
        shouldFetch,
        distance: Math.round(distance * 10) / 10,
        distanceThreshold,
        radiusDiff: Math.abs(newViewport.radius - lastFetch.radius),
        radiusThreshold,
        timeSinceLastFetch: Math.round((Date.now() - lastFetch.lastFetchTime) / 1000),
        timeThresholdSeconds: timeThreshold / 1000,
      });

      return shouldFetch;
    },
    [radius],
  );

  /**
   * Fetch events from API
   */
  const {
    data: apiData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    remove,
    error,
  } = useAllEvents({
    distance_km: isNeighbourhood || isPyxiSelect ? 1000000000000 : radius,
    tab: isNeighbourhood 
      ? TABS.NEIGHBOURHOOD_EVENT 
      : isPyxiSelect 
        ? TABS.PYXI_SELECT_EVENT 
        : TABS.DISCOVER,
    order_by: ORDER_BY.START_DATE,
    order_dir: ORDER_DIR.ASC,
    event_age_group: isForKids ? EVENT_AGE_GROUP.CHILDREN : undefined,
    filter_type:
      selectedTimeframe?.value === t('home.upcoming_events') ||
      isNeighbourhood ||
      isPyxiSelect ||
      isPyxiCore
        ? undefined
        : selectedTimeframe?.value,
    q: globalInputValue,
    isNeighbourhood: isNeighbourhood,
    enabled: enabled && !!userLocation,
    timeframe:
      selectedTimeframe?.value === t('home.upcoming_events')
        ? 'future'
        : selectedTimeframe?.value === t('events.past')
          ? 'past'
          : selectedTimeframe.title === t('events.pyxi_core') ? 'future' : undefined,
    isMap: true,
    event_service_type: eventServiceType || (isPyxiCore ? 'GENERAL' : undefined) // Use passed eventServiceType or fallback to GENERAL for PyXi Core
  });

  /**
   * Update map viewport and fetch events if needed
   */
  const updateMapViewport = useCallback(
    (region: Region) => {
      const zoomLevel = calculateZoomLevel(region);
      const newRadius = calculateRadiusFromZoom(zoomLevel);

      const newViewport: MapViewportState = {
        center: {
          latitude: region.latitude,
          longitude: region.longitude,
        },
        radius: newRadius,
        lastFetchTime: Date.now(),
        zoomLevel,
      };

      // Update current radius if it changed
      if (newRadius !== currentRadius) {
        setCurrentRadius(newRadius);
      }

      // Check if we need to fetch new events
      if (shouldFetchNewEvents(newViewport)) {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Debounce the fetch to avoid too many API calls
        debounceTimeoutRef.current = setTimeout(() => {
          setMapViewport(newViewport);
          lastFetchRef.current = newViewport;
          refetch();
        }, 500);
      }
    },
    [calculateZoomLevel, calculateRadiusFromZoom, currentRadius, shouldFetchNewEvents, refetch],
  );

  return {
    data: apiData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    remove,
    updateMapViewport,
    currentRadius,
  };
};
